"use client";

import fetchGraphqlClient from "@workspace/nextjs_utils/src/fetchGraphqlClient";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  EVENT_SEARCH_MODAL_CLOSING,
  EVENT_SEARCH_MODAL_LOADING,
  EVENT_SEARCH_MODAL_OPENING,
  EVENT_SEARCH_MODAL_USERINPUT,
  HIDDEN_PROVIDERS,
  ITEMS_PER_PAGE,
} from "../constants";
import listenForSearchModalEvent from "../lib/listenForSearchModalEvents";
import propagateSearchModalClose from "../lib/propagateSearchModalClose";
import propagateSearchModalOpen from "../lib/propagateSearchModalOpen";
import propagateSearchModalSurprise from "../lib/propagateSearchModalSurprise";
import resolveMenuItem from "../lib/resolveMenuItem";
import { PROVIDER_GAMES_QUERY } from "../queries/providerGames";
import { PROVIDERS_QUERY } from "../queries/providers";
import type { GameFragment, ProviderFragment } from "../types";
import { useQuery } from "./useQuery";

type CasinoGameProvidersResult = {
  casinoGameInventory?: {
    gameProvidersWithGames?: ProviderFragment[] | null;
  } | null;
};

type CasinoGameProvidersWithGamesResult = {
  casinoGameInventory?: {
    gameProvidersWithGames?:
      | ({
          name?: string | null;
          handle?: string | null;
          games?: (GameFragment | null)[] | null;
        } | null)[]
      | null;
  } | null;
};

const useSearchModal = (
  brand: string | undefined,
  countryCode: string | undefined,
) => {
  const [currentState, setCurrentState] = useState<boolean>(false);
  const pathname = usePathname();
  const [currentCategory, setCurrentCategory] = useState<string>("all");
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [overallLoading, setLoading] = useState<boolean>(false);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [fetchedProviders, setFetchedProviders] = useState<ProviderFragment[]>(
    [],
  );
  const allProviders = useMemo(
    () =>
      fetchedProviders.filter(
        (provider) =>
          !provider.handle || !HIDDEN_PROVIDERS.includes(provider.handle),
      ),
    [fetchedProviders],
  );
  const [currentVisibleItems, setCurrentVisibleItems] = useState<
    GameFragment[]
  >([]);
  const [providerGamesData, setProviderGamesData] = useState<GameFragment[]>(
    [],
  );
  const [loadingProviderGames, setLoadingProviderGames] =
    useState<boolean>(false);
  const [pagination, setPagination] = useState<{
    first: number;
    offset: number;
  }>({
    first: ITEMS_PER_PAGE,
    offset: 0,
  });
  const providerSwitchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const currentSearchTermRef = useRef("");
  const resolvedBrand = brand || "bitcasino";
  const resolvedCategory = resolveMenuItem(currentCategory, resolvedBrand);

  const selectedProviderHandles = useMemo(() => {
    const handles: string[] = [];
    for (const selected of selectedProviders) {
      const provider = allProviders.find(
        (candidate) => (candidate.name || candidate.handle || "") === selected,
      );
      if (provider?.handle) {
        handles.push(provider.handle);
      }
    }
    return handles;
  }, [selectedProviders, allProviders]);

  const [data, loading, itemsNumber] = useQuery({
    queryType: "search",
    searchTerm: currentSearchTerm,
    category: resolvedCategory,
    brand: resolvedBrand,
    countryCode: countryCode,
    pagination,
    selectedProviders,
    skip: currentSearchTerm === "",
  });
  const [topgamesData, loadingTopGames] = useQuery({
    queryType: "top_games",
    brand: resolvedBrand,
    category: resolvedCategory,
    selectedProviders,
    pagination: { first: 32, offset: 0 },
  });

  const loadMoreAvailable = useMemo(() => {
    if (currentSearchTerm === "") return false;
    return (pagination?.offset || 0) + ITEMS_PER_PAGE - 1 < itemsNumber;
  }, [itemsNumber, currentSearchTerm, pagination?.offset]);

  const loadMore = () => {
    setPagination((previous) => {
      if (!loading) {
        return {
          first: ITEMS_PER_PAGE,
          offset: ITEMS_PER_PAGE + previous.offset,
        };
      } else {
        return previous;
      }
    });
  };

  useEffect(() => {
    window.__search_modal_open = currentState;
  }, [currentState]);

  useEffect(() => {
    setCurrentState(false);
  }, [pathname]);

  useEffect(() => {
    return () => {
      if (providerSwitchTimeoutRef.current) {
        clearTimeout(providerSwitchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    let ignore = false;
    const fetchProviders = async (): Promise<void> => {
      try {
        const d = await fetchGraphqlClient<CasinoGameProvidersResult>(
          PROVIDERS_QUERY,
          "gameProvidersWithGames",
          {
            variables: { site: resolvedBrand, gameFilter: { currency: "USD" } },
          },
        );
        if (!ignore) {
          setFetchedProviders(
            d?.data?.casinoGameInventory?.gameProvidersWithGames || [],
          );
        }
      } catch {
        if (!ignore) {
          setFetchedProviders([]);
        }
      }
    };
    fetchProviders();
    return () => {
      ignore = true;
    };
  }, [resolvedBrand]);

  useEffect(() => {
    if (currentSearchTerm !== "" || selectedProviders.length === 0) {
      setProviderGamesData([]);
      return;
    }
    if (selectedProviderHandles.length === 0) {
      return;
    }
    let ignore = false;
    setLoadingProviderGames(true);
    const fetchProviderGames = async (): Promise<void> => {
      try {
        const d = await fetchGraphqlClient<CasinoGameProvidersWithGamesResult>(
          PROVIDER_GAMES_QUERY,
          "gameProvidersWithGames",
          {
            variables: {
              site: resolvedBrand,
              menuItemFilter: {
                handles: selectedProviderHandles,
              },
              gameFilter: {
                currency: "USD",
              },
            },
          },
        );
        if (!ignore) {
          const providers =
            d?.data?.casinoGameInventory?.gameProvidersWithGames || [];
          setProviderGamesData(
            providers
              .flatMap((p) => p?.games || [])
              .filter((game): game is GameFragment => Boolean(game)),
          );
        }
      } catch {
        if (!ignore) {
          setProviderGamesData([]);
        }
      } finally {
        if (!ignore) {
          setLoadingProviderGames(false);
        }
      }
    };
    fetchProviderGames();
    return () => {
      ignore = true;
    };
  }, [
    selectedProviderHandles,
    selectedProviders,
    currentSearchTerm,
    resolvedBrand,
  ]);

  useEffect(() => {
    if (currentSearchTerm !== "") {
      if (Array.isArray(data)) {
        setCurrentVisibleItems((prev) => {
          if (pagination.offset === 0) {
            return data;
          } else {
            const adArray = [...prev];
            data.forEach((obj) => {
              if (!adArray.some((item) => item.id === obj.id)) {
                adArray.push(obj);
              }
            });
            return adArray;
          }
        });
        // This small timeout needs to Search to patch together the loading status from the
        // query and the loading from the input.
        if (window.__search_loading_timeout) {
          clearTimeout(window.__search_loading_timeout);
        }
        window.__search_loading_timeout = setTimeout(
          () => setLoading(false),
          400,
        );
      }
    } else if (selectedProviders.length > 0) {
      setCurrentVisibleItems(providerGamesData);
      setLoading(false);
    } else {
      setCurrentVisibleItems([]);
      setLoading(false);
    }
  }, [
    currentSearchTerm,
    pagination.offset,
    data,
    selectedProviders,
    providerGamesData,
  ]);

  useEffect(() => {
    const handleFunc = (event: Event | CustomEvent) => {
      switch (event.type) {
        case EVENT_SEARCH_MODAL_CLOSING:
          setCurrentState(false);
          break;
        case EVENT_SEARCH_MODAL_OPENING:
          setCurrentState(true);
          break;
        case EVENT_SEARCH_MODAL_LOADING:
          setLoading(true);
          break;
        default:
      }
    };
    const mappedEvents = listenForSearchModalEvent(handleFunc);
    return () => {
      mappedEvents.forEach((eventName: string) => {
        window.removeEventListener(eventName, handleFunc);
      });
    };
  }, []);

  const setCategory = (category: string = "all") => {
    if (category !== currentCategory) {
      setSelectedProviders([]);
    }
    setCurrentCategory(category);
  };

  const toggleProvider = (providerId: string) => {
    if (providerSwitchTimeoutRef.current) {
      clearTimeout(providerSwitchTimeoutRef.current);
    }
    setLoading(true);
    providerSwitchTimeoutRef.current = setTimeout(() => setLoading(false), 500);

    setSelectedProviders((prev: string[]) => {
      if (!providerId || typeof providerId !== "string") {
        return prev;
      }
      if (!prev.length) {
        return [providerId];
      }

      if (prev.includes(providerId)) {
        return prev.filter((item: string) => item !== providerId);
      }

      return [...prev, ...[providerId]];
    });
  };

  const searchTerm = (input: string) => {
    const isSameTerm = input === currentSearchTermRef.current;
    currentSearchTermRef.current = input;

    if (isSameTerm) {
      setLoading(false);
    }

    if (input === "") {
      setCurrentSearchTerm("");
      setPagination({ first: ITEMS_PER_PAGE, offset: 0 });
      return;
    }
    setCurrentSearchTerm(input);

    if (!isSameTerm) {
      setCurrentVisibleItems([]);
    }

    // Reset the pagination on the new search, important to do!
    setPagination({ first: ITEMS_PER_PAGE, offset: 0 });
    const event = new CustomEvent(EVENT_SEARCH_MODAL_USERINPUT, {
      detail: {
        timestamp: Date.now(),
        txt: input,
      },
    });
    window.dispatchEvent(event);
  };

  const toggleFunc = () => {
    if (!currentState) {
      propagateSearchModalOpen();
    } else {
      propagateSearchModalClose();
    }
  };

  const finalItems = useMemo(() => {
    return [
      ...(topgamesData || []).map((item) => ({ ...item, __type: "topgame" })),
      ...(currentVisibleItems || []).map((item) => ({
        ...item,
        __type: "search",
      })),
    ].filter((game) => !!game.href);
  }, [selectedProviders, topgamesData, currentVisibleItems]);

  const surpriseMeFunc = () => {
    const buffer =
      currentSearchTerm === "" && selectedProviders.length > 0
        ? providerGamesData
        : finalItems;

    const randomIndex = Math.floor(Math.random() * buffer.length);
    const randomItem = buffer?.[randomIndex];
    if (randomItem?.href) {
      propagateSearchModalSurprise(randomItem.href);
    }
  };

  // This is a overall QOL, if there is no search term, we care only about 1 loading, not others
  const loadingStatus =
    currentSearchTerm === "" && selectedProviders.length === 0
      ? loadingTopGames
      : currentSearchTerm === ""
        ? loadingProviderGames || overallLoading
        : loading || loadingTopGames || overallLoading;

  const searchItemsCount =
    currentSearchTerm === "" && selectedProviders.length > 0
      ? providerGamesData.length
      : itemsNumber;

  return {
    currentState,
    toggle: toggleFunc,
    searchTerm,
    setCategory,
    totalSearchItems: searchItemsCount,
    loading: loadingStatus,
    loadMore,
    loadMoreAvailable,
    currentSearchTerm,
    currentVisibleItems: finalItems,
    surpriseMeFunc,
    toggleProvider,
    currentSelectedProviders: selectedProviders,
    allProviders,
  };
};

export { useSearchModal };
